// src/Sections/Foundation/Foundation.jsx - Vision Foundation Page
import React, { useEffect, useRef, useState } from "react";
import "./Foundation.css";
import { useTranslation } from "react-i18next";
import { blog2, blog3, blog4 } from "../../assets";

// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y, Keyboard } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

/* ---- tiny reveal-on-scroll hook ---- */
function useReveal(options = { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) return;
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView, options]);

  return [ref, inView];
}

export default function Foundation() {
  const { t } = useTranslation();

  // reveals
  const [titleRef, titleIn] = useReveal();
  const [heroSectionRef, heroSectionIn] = useReveal();
  const [contentRef, contentIn] = useReveal();
  const [carouselRef, carouselIn] = useReveal();

  // Swiper state
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  // Handle swiper state changes
  const handleSlideChange = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const goPrev = () => {
    if (swiperInstance) swiperInstance.slidePrev();
  };
  const goNext = () => {
    if (swiperInstance) swiperInstance.slideNext();
  };

  // Sample quotes data - you can move this to data.js later
  const quotes = [
    {
      text: t('foundation.quotes.maximus'),
      author: "Maximus Trinchini"
    },
    {
      text: t('foundation.quotes.christophe'),
      author: "Christophe Lavoie"
    },
    {
      text: t('foundation.quotes.kevin'),
      author: "Kevin Wilson"
    }
  ];

  // Foundation carousel photos
  const carouselPhotos = [
    { id: 1, src: require('../../assets/FoundationPics/IMG_0467.jpeg'), alt: "Foundation Event 1" },
    { id: 2, src: require('../../assets/FoundationPics/foodbox.jpg'), alt: "Food Donation Box" },
    { id: 3, src: require('../../assets/FoundationPics/IMG_0476.jpeg'), alt: "Foundation Event 3" },
    { id: 4, src: require('../../assets/FoundationPics/IMG_0478.jpeg'), alt: "Foundation Event 4" },
    { id: 5, src: require('../../assets/FoundationPics/IMG_0479.jpeg'), alt: "Foundation Event 5" },
    { id: 6, src: require('../../assets/FoundationPics/IMG_0480.jpeg'), alt: "Foundation Event 6" },
  ];

  // Related blog posts - matching the 3 posts next to Vision Foundation on main blog page
  const relatedPosts = [
    {
      title: t('blogs.breakingBoundaries.title'),
      slug: "breaking-boundaries",
      image: blog2,
      date: "2025-09-15",
      readTime: 6
    },
    {
      title: t('blogs.nextGenTraining.title'),
      slug: "building-the-next-generation",
      image: blog4,
      date: "2025-08-18",
      readTime: 7
    },
    {
      title: t('blogs.storiesFromField.title'),
      slug: "stories-from-the-field",
      image: blog3,
      date: "2025-09-01",
      readTime: 5
    }
  ];

  return (
    <section id="foundation" aria-labelledby="foundation-heading">
      <div className="foundation__container">
        
        {/* Title Section */}
        <div
          ref={titleRef}
          className={`foundation__title reveal reveal--up ${titleIn ? "in" : ""}`}
        >
          <div className="foundation__badge reveal-item">{t('foundation.badge')}</div>
          <h1 id="foundation-heading" className="foundation__mainTitle reveal-item">
            {t('foundation.title')}
          </h1>
          <p className="foundation__subtitle reveal-item">
            {t('foundation.subtitle')}
          </p>
        </div>

        {/* Hero Content Section */}
        <div
          ref={heroSectionRef}
          className={`foundation__heroSection reveal reveal--up ${heroSectionIn ? "in" : ""}`}
        >
          {/* Photo Box on Left with Title Above */}
          <div className="foundation__photoBox reveal-item" style={{ transitionDelay: "80ms" }}>
            <h2 className="foundation__heroTitle">{t('foundation.heroTitle')}</h2>
            
            {/* Foundation Photo */}
            <div className="foundation__photoContainer">
              <img 
                src={require('../../assets/FoundationPics/Squadcharity.jpeg')} 
                alt="Vision Foundation Charity Squad" 
                className="foundation__photo"
              />
            </div>

            {/* About Foundation Paragraph */}
            <div className="foundation__aboutText">
              <h3 className="foundation__aboutTitle">{t('foundation.aboutTitle')}</h3>
              <p>{t('foundation.aboutText1')}</p>
              <p>{t('foundation.aboutText2')}</p>
            </div>
          </div>

          {/* Middle: Quotes */}
          <div className="foundation__quotesColumn reveal-item" style={{ transitionDelay: "160ms" }}>
            <div className="foundation__quotes">
              {quotes.slice(0, 3).map((quote, index) => (
                <blockquote key={index} className="foundation__quote">
                  <p>"{quote.text}"</p>
                  <cite>- {quote.author}</cite>
                </blockquote>
              ))}
            </div>
            
            {/* Donate Button */}
            <a href="/contact" className="foundation__donateButton">
              {t('foundation.donateButton')}
            </a>
            
            {/* Donors Call-to-Action */}
            <div 
              className="foundation__donorsCallout"
              onClick={() => {
                const donorsPlaque = document.querySelector('.foundation__donorsPlaque');
                if (donorsPlaque) {
                  donorsPlaque.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  const donorsPlaque = document.querySelector('.foundation__donorsPlaque');
                  if (donorsPlaque) {
                    donorsPlaque.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }
              }}
              aria-label="Scroll to donors section"
            >
              <span className="foundation__donorsText">{t('foundation.donorsCallout')}</span>
              <span className="foundation__donorsArrow">▼</span>
            </div>
          </div>

          {/* Far Right: Blog Posts */}
          <div className="foundation__postsColumn reveal-item" style={{ transitionDelay: "240ms" }}>
            <div className="foundation__relatedPosts">
              <h3 className="foundation__relatedTitle">{t('foundation.relatedTitle')}</h3>
              {relatedPosts.map((post, index) => (
                <article key={index} className="foundation__postCard">
                  <a href={`/blog/${post.slug}`} className="foundation__postImageLink">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="foundation__postImage"
                      loading="lazy"
                    />
                  </a>
                  <h4 className="foundation__postTitle">
                    <a href={`/blog/${post.slug}`}>{post.title}</a>
                  </h4>
                  <div className="foundation__postMeta">
                    <time className="foundation__postDate" dateTime={post.date}>
                      {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </time>
                    <span className="foundation__postSep"> | </span>
                    <span className="foundation__postRead">{t('blogs.readTime', { min: post.readTime })}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>

        {/* Photo Carousel - Full Width */}
        <div
          ref={carouselRef}
          className={`foundation__carousel-wrap reveal reveal--up ${carouselIn ? "in" : ""}`}
        >
          <Swiper
              className="foundation__carousel"
              modules={[Navigation, A11y, Keyboard]}
              onSwiper={(swiper) => {
                setSwiperInstance(swiper);
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);
              }}
              onSlideChange={handleSlideChange}
              navigation={false}
              keyboard={{ enabled: true }}
              a11y={{ enabled: true }}
              spaceBetween={18}
              loop={carouselPhotos.length > 2}
              breakpoints={{
                0: { slidesPerView: 1, spaceBetween: 0 },
                480: { slidesPerView: 1, spaceBetween: 0 },
                640: { slidesPerView: 2, spaceBetween: 16 },
                900: { slidesPerView: 3, spaceBetween: 18 },
                1200: { slidesPerView: 4, spaceBetween: 20 },
              }}
            >
              {carouselPhotos.map((photo, i) => (
                <SwiperSlide key={photo.id} aria-label={photo.alt}>
                  <div
                    className="foundation__carousel-card reveal-item"
                    style={{ transitionDelay: `${100 + (i % 6) * 80}ms` }}
                  >
                    <img 
                      src={photo.src} 
                      alt={photo.alt}
                      className="foundation__carousel-image"
                    />
                  </div>
                </SwiperSlide>
              ))}
          </Swiper>

          {/* Custom Navigation Buttons */}
          <button
            className={`foundation__nav-btn foundation__nav-btn--prev ${isBeginning ? "disabled" : ""}`}
            onClick={goPrev}
            disabled={isBeginning}
            aria-label="Previous photos"
          >
            ‹
          </button>
          <button
            className={`foundation__nav-btn foundation__nav-btn--next ${isEnd ? "disabled" : ""}`}
            onClick={goNext}
            disabled={isEnd}
            aria-label="Next photos"
          >
            ›
          </button>
        </div>

        {/* Two Column Paragraph Section */}
        <div
          ref={contentRef}
          className={`foundation__contentWrapper reveal reveal--up ${contentIn ? "in" : ""}`}
        >
          {/* Left Column - Paragraph 2 (Our Current Mission) */}
          <div className="foundation__paragraph reveal-item" style={{ transitionDelay: "80ms" }}>
            <h3 className="foundation__sectionTitle">{t('foundation.mission.title')}</h3>
            <p>{t('foundation.mission.text1')}</p>
            <ul className="foundation__essentialsList">
              <li><strong>{t('foundation.mission.clothes')}</strong></li>
              <li><strong>{t('foundation.mission.books')}</strong></li>
              <li><strong>{t('foundation.mission.food')}</strong></li>
            </ul>
            <p>{t('foundation.mission.text2')}</p>
            <p>{t('foundation.mission.text3')}</p>
          </div>

          {/* Right Column - Paragraph 3 */}
          <div className="foundation__paragraph reveal-item" style={{ transitionDelay: "160ms" }}>
            <h3 className="foundation__sectionTitle">{t('foundation.organizations.title')}</h3>
            <p>{t('foundation.organizations.text')}</p>
            <ul className="foundation__organizationsList">
              <li>Ricochet</li>
              <li>Porte Ouverte</li>
              <li>Old Brewery Mission</li>
              <li>Résilience Montréal</li>
              <li>On Rock Community Services</li>
              <li>Maison Benoît Labre</li>
            </ul>

            {/* Organization Logos Gallery */}
            <div className="foundation__logoGallery">
              <div className="foundation__logoCard">
                <img src={require('../../assets/FoundationPics/ricochet.png')} alt="Ricochet" />
              </div>
              <div className="foundation__logoCard">
                <img src={require('../../assets/FoundationPics/Laporteouvert.png')} alt="Porte Ouverte" />
              </div>
              <div className="foundation__logoCard">
                <img src={require('../../assets/FoundationPics/Mission_Old_Brewery.png')} alt="Old Brewery Mission" />
              </div>
              <div className="foundation__logoCard">
                <img src={require('../../assets/FoundationPics/resilience-banner.png')} alt="Résilience Montréal" />
              </div>
              <div className="foundation__logoCard">
                <img src={require('../../assets/FoundationPics/images.jpg')} alt="On Rock Community Services" />
              </div>
              <div className="foundation__logoCard">
                <img src={require('../../assets/FoundationPics/la_maison_benoit_labre_logo.jpg')} alt="Maison Benoît Labre" />
              </div>
            </div>
          </div>
        </div>

        {/* Donors Plaque - Full Width Below Both Paragraphs */}
        <div className="foundation__donorsPlaque">
          <h3 className="foundation__donorsTitle">{t('foundation.donors.title')}</h3>
          <p className="foundation__donorsIntro">{t('foundation.donors.intro')}</p>
          <ul className="foundation__donorsList">
            <li>Kevin Wilson</li>
            <li>Christophe Lavoie</li>
            <li>Renzo Castillo</li>
            <li>Maximus Trinchini</li>
            <li>Manny Trinchini</li>
            <li>Luc Parise</li>
            <li>Destiny Hull</li>
            <li>Jack Briggs</li>
            <li>Jan Nozewski</li>
            <li>Alex Nozewski</li>
            <li>Sarah Haddad</li>
            <li>Spencer Letourneau</li>
            <li>Justin Stringer</li>
            <li>Nick Maniatis</li>
            <li>Julia King</li>
            <li>Sonja Nilsson</li>
            <li>Lina Nilsson</li>
            <li>Dante Rozzi</li>
            <li>Caitlyn Batty</li>
            <li>Mike Potts</li>
            <li>Alexandra Potts</li>
            <li>Anne Mcgreevi</li>
            <li>Genesis Lazaro</li>
            <li>Juliana Azedo</li>
          </ul>
          <p className="foundation__donorsFootnote">{t('foundation.donors.footnote')}</p>
        </div>

      </div>
    </section>
  );
}
